import { sha256 } from '@noble/hashes/sha256';

// src/singleton/index.ts
var NAMESPACE = "@authe/fe-commons/singleton/";
function globalSingleton(key, init) {
  const slot = /* @__PURE__ */ Symbol.for(NAMESPACE + key);
  const realm = globalThis;
  const existing = realm[slot];
  if (existing !== void 0) return existing;
  const created = init();
  realm[slot] = created;
  return created;
}
var BRAND_NAMESPACE = "@authe/fe-commons/brand/";
var ordinaryHasInstance = Function.prototype[Symbol.hasInstance];
function brandClass(cls, key) {
  const brand = /* @__PURE__ */ Symbol.for(BRAND_NAMESPACE + key);
  Object.defineProperty(cls.prototype, brand, { value: true });
  Object.defineProperty(cls, Symbol.hasInstance, {
    value(value) {
      if (this !== cls) return ordinaryHasInstance.call(this, value);
      return typeof value === "object" && value !== null && value[brand] === true;
    }
  });
}

// src/errors/index.ts
var ErrorCategory = {
  VALIDATION: "validation",
  AUTHENTICATION: "authentication",
  AUTHORIZATION: "authorization",
  NOT_FOUND: "not_found",
  CONFLICT: "conflict",
  RATE_LIMIT: "rate_limit",
  EXTERNAL_SERVICE: "external_service",
  DATABASE: "database",
  CONFIGURATION: "configuration",
  INTERNAL: "internal",
  DATA: "data",
  PIPELINE: "pipeline"
};
var DOCS_BASE = "https://docs.authe.io/errors";
function spec(code, description, userAction, category, defaultStatus, opts = {}) {
  return {
    code,
    description,
    userAction,
    documentationUrl: `${DOCS_BASE}/${code}`,
    category,
    defaultStatus,
    retryable: opts.retryable ?? false,
    severity: opts.severity ?? "ERROR"
  };
}
var ErrorCode = {
  // Validation
  VAL001: spec("VAL001", "Missing required field", "Provide all required fields and retry the request.", ErrorCategory.VALIDATION, 400, { severity: "WARNING" }),
  VAL002: spec("VAL002", "Invalid field format", "Check the field format documentation and correct the value.", ErrorCategory.VALIDATION, 400, { severity: "WARNING" }),
  VAL003: spec("VAL003", "Invalid field value", "Use one of the allowed values listed in the documentation.", ErrorCategory.VALIDATION, 400, { severity: "WARNING" }),
  VAL004: spec("VAL004", "Invalid response structure", "An internal response could not be parsed; retry, then contact support if persistent.", ErrorCategory.VALIDATION, 500),
  // Authentication
  AUTH001: spec("AUTH001", "Invalid credentials", "Sign in again with valid credentials.", ErrorCategory.AUTHENTICATION, 401),
  AUTH002: spec("AUTH002", "Token expired", "Refresh your session and retry.", ErrorCategory.AUTHENTICATION, 401),
  AUTH003: spec("AUTH003", "Invalid token", "Sign out, sign back in, and retry.", ErrorCategory.AUTHENTICATION, 401),
  // Authorization
  PERM001: spec("PERM001", "Insufficient permissions", "Ask an admin to grant the required permission, or use a different account.", ErrorCategory.AUTHORIZATION, 403),
  // NOT a tenancy refusal, despite what this line used to say ("You do not have
  // access to this resource within your tenant"). PERM002 is the authorizer's
  // general deny: a wrong audience, an app/env or application-type mismatch, an
  // expired or incomplete impersonation session, a revoked API key, an AWS
  // account that is not allowed, a blue-route mismatch, an unregistered public
  // API, or a gateway not whitelisted for the calling application. None of those
  // is tenancy — though a handler-level cross-tenant refusal carries PERM002
  // too, so tenancy is one cause among many and the line can no longer name
  // just that one. Mirrors `commons.errors.codes`.
  PERM002: spec("PERM002", "Resource access denied", "Refused before the service ran. Signing in again clears an expired or mismatched session; otherwise the resource belongs to another tenant, or this application isn't registered to reach that API.", ErrorCategory.AUTHORIZATION, 403),
  // Packaging, not permission. PERM001's remedy ("ask an admin to grant the
  // required permission") is actively wrong for a plan refusal: nobody in the
  // customer's own org can grant an entitlement their subscription lacks.
  PERM003: spec("PERM003", "Not included in your plan", "This feature is not part of your current subscription. Contact sales to add it.", ErrorCategory.AUTHORIZATION, 403),
  // A cap, not an absence. PERM003 says the feature is not yours; this says it
  // is, and it is full — so the remedy is to free a slot or move tier, never to
  // retry (which is why this is not the 429 RATE001).
  PERM004: spec("PERM004", "Plan limit reached", "Remove an existing item to free a slot, or upgrade for a higher limit.", ErrorCategory.AUTHORIZATION, 403),
  // Resource
  RES001: spec("RES001", "Resource not found", "Verify the identifier; the resource may have been deleted or never existed.", ErrorCategory.NOT_FOUND, 404),
  RES002: spec("RES002", "Resource already exists", "Use a different identifier or update the existing resource instead.", ErrorCategory.CONFLICT, 409, { severity: "WARNING" }),
  // Rate limiting
  RATE001: spec("RATE001", "Rate limit exceeded", "Slow request rate and retry after the `Retry-After-Ms` interval.", ErrorCategory.RATE_LIMIT, 429, { retryable: true, severity: "WARNING" }),
  // A plan's usage quota is used up for its window. 429, but NOT retryable: an
  // unknown 429 is retried by the client, and re-sending a refused run cannot
  // clear a monthly cap. `details.resets_at` says when it does.
  QUOTA001: spec("QUOTA001", "Usage quota reached", "Wait until the quota resets, or move to a plan with a higher quota. The message says which quota and when it resets.", ErrorCategory.RATE_LIMIT, 429, { severity: "WARNING" }),
  // External service
  EXT001: spec("EXT001", "External service unavailable", "An upstream provider is unavailable; retry the request, then contact support if persistent.", ErrorCategory.EXTERNAL_SERVICE, 503, { retryable: true }),
  EXT002: spec("EXT002", "External service timeout", "An upstream provider timed out; retry, then contact support if persistent.", ErrorCategory.EXTERNAL_SERVICE, 504, { retryable: true }),
  // Database
  DB001: spec("DB001", "Database connection error", "Retry the request; if persistent, contact support.", ErrorCategory.DATABASE, 502, { retryable: true }),
  DB002: spec("DB002", "Database query error", "An internal query failed; retry and contact support if persistent.", ErrorCategory.DATABASE, 500),
  DB003: spec("DB003", "Database throttled", "Capacity throttled the request; retry after `Retry-After-Ms`.", ErrorCategory.DATABASE, 429, { retryable: true, severity: "WARNING" }),
  // Configuration / Internal
  CFG001: spec("CFG001", "Service configuration error", "An internal misconfiguration was detected; this is a server-side bug. Contact support.", ErrorCategory.CONFIGURATION, 500),
  INT001: spec("INT001", "Internal server error", "An unexpected error occurred. Retry, then contact support if persistent.", ErrorCategory.INTERNAL, 500),
  // Pipeline + data
  PIPE001: spec("PIPE001", "Pipeline processing error", "A pipeline step failed. The transaction will be retried automatically; contact support if it does not recover.", ErrorCategory.PIPELINE, 500, { retryable: true }),
  DATA001: spec("DATA001", "Data integrity error", "An expected invariant on the data was violated. This is a server-side bug; contact support.", ErrorCategory.DATA, 500)
};
function getCodeSpec(code) {
  for (const member of Object.values(ErrorCode)) {
    if (member.code === code) return member;
  }
  return null;
}
function errorCodeOf(error) {
  for (const candidate of [error.code, error.error_code]) {
    if (candidate && getCodeSpec(candidate)) return candidate;
  }
  return error.error_code ?? null;
}
(class _AutheError extends Error {
  static {
    brandClass(this, "errors/AutheError");
  }
  spec;
  details;
  correlationId;
  constructor(spec2, opts = {}) {
    super(opts.message ?? spec2.description);
    this.name = "AutheError";
    this.spec = spec2;
    this.details = opts.details ?? {};
    this.correlationId = opts.correlationId;
    if (opts.cause !== void 0) this.cause = opts.cause;
  }
  /** Look up a code by string id and build an `AutheError` (falls back to INT001). */
  static fromCode(code, opts = {}) {
    return new _AutheError(getCodeSpec(code) ?? ErrorCode.INT001, opts);
  }
  get retryable() {
    return this.spec.retryable;
  }
  toBody() {
    return {
      message: this.message,
      error_type: this.name,
      error_code: this.spec.code,
      error_level: this.spec.severity,
      details: this.details,
      correlation_id: this.correlationId ?? null
    };
  }
  toResponse() {
    return { statusCode: this.spec.defaultStatus, ok: false, error: this.toBody() };
  }
});

// src/api/unwrap.ts
var ApiError = class extends Error {
  constructor(envelope) {
    super(envelope.error.message);
    this.envelope = envelope;
    this.name = "ApiError";
    this.statusCode = envelope.statusCode;
    this.code = errorCodeOf(envelope.error) ?? void 0;
    this.level = envelope.error.error_level;
    this.details = envelope.error.details;
    this.correlationId = envelope.error.correlation_id;
  }
  envelope;
  static {
    brandClass(this, "api/ApiError");
  }
  statusCode;
  code;
  level;
  details;
  correlationId;
};
function unwrapResponse(res) {
  if (res.ok) return res;
  throw new ApiError(res);
}
function unwrapData(res) {
  return unwrapResponse(res).data;
}

// src/api/wire.ts
function isRecord(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
function rowsFromWire(data) {
  const rows = Array.isArray(data) ? data : isRecord(data) ? data.items : void 0;
  return Array.isArray(rows) ? rows.filter(isRecord) : [];
}

// src/api/paging.ts
var WALK_PAGE_SIZE = 100;
var WALK_MAX_PAGES = 20;
var IncompleteWalkError = class extends Error {
  constructor(path, pages) {
    super(`${path}: still paging after ${pages} pages`);
    this.path = path;
    this.pages = pages;
    this.name = "IncompleteWalkError";
  }
  path;
  pages;
  static {
    brandClass(this, "api/IncompleteWalkError");
  }
};
async function walkPages(client, path, {
  query,
  signal,
  pageSize = WALK_PAGE_SIZE,
  maxPages = WALK_MAX_PAGES,
  rowsOf = rowsFromWire,
  cacheTtlMs,
  requireComplete = false
} = {}) {
  const rows = [];
  let cursor;
  for (let page = 0; page < maxPages; page += 1) {
    const params = {
      ...query ?? {},
      limit: String(pageSize),
      ...cursor ? { cursor } : {}
    };
    const payload = unwrapData(
      await client.get(path, {
        query: params,
        signal,
        ...cacheTtlMs === void 0 ? {} : { cacheTtlMs }
      })
    );
    rows.push(...rowsOf(payload));
    const next = isRecord(payload) ? payload.next_cursor : void 0;
    cursor = typeof next === "string" && next ? next : void 0;
    if (!cursor) break;
  }
  if (cursor && requireComplete) throw new IncompleteWalkError(path, maxPages);
  return rows;
}

// src/grouping-rules/wire.ts
function asRecord(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value) ? value : {};
}
function asStringOr(value, fallback = "") {
  return value == null ? fallback : String(value);
}
function asNullableString(value) {
  return value == null ? null : String(value);
}
function asOptString(value) {
  return value == null ? void 0 : String(value);
}
function asStringList(value) {
  return Array.isArray(value) ? value.filter((v) => typeof v === "string") : [];
}

// src/grouping-rules/source.ts
var STANDING_STATES = [
  "in_effect",
  "shadowed",
  "disabled",
  "invalid",
  "not_applicable"
];
function num(value, fallback = 0) {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}
function standingFromWire(raw) {
  if (!raw || typeof raw !== "object") return null;
  const s = raw;
  const state = STANDING_STATES.find((known) => known === s.state);
  if (!state) return null;
  return {
    state,
    sagaKind: asStringOr(s.saga_kind),
    position: num(s.position, 0),
    chainIds: asStringList(s.chain_ids),
    exceptChainIds: asStringList(s.except_chain_ids),
    shadowedBy: asStringList(s.shadowed_by),
    problems: asStringList(s.problems),
    takesOverIfDeleted: asStringList(s.takes_over_if_deleted)
  };
}
function ruleFromWire(raw) {
  return {
    id: asStringOr(raw.id),
    name: asStringOr(raw.name),
    description: asNullableString(raw.description),
    scope: asStringOr(raw.scope, "tenant"),
    enabled: raw.enabled !== false,
    priority: num(raw.priority, 100),
    sagaKind: asNullableString(raw.saga_kind),
    relationshipType: asNullableString(raw.relationship_type),
    chainIds: asStringList(raw.chain_ids),
    spec: asRecord(raw.spec),
    matchCount: num(raw.match_count, 0),
    lastMatchedAt: typeof raw.last_matched_at === "number" ? raw.last_matched_at : null,
    updatedAt: typeof raw.updated_at === "number" ? raw.updated_at : null,
    updatedBy: asNullableString(raw.updated_by),
    standing: standingFromWire(raw.standing),
    canEdit: typeof raw.can_edit === "boolean" ? raw.can_edit : void 0
  };
}
function toWire(input) {
  const body = {};
  if (input.name !== void 0) body.name = input.name;
  if (input.description !== void 0) body.description = input.description;
  if (input.scope !== void 0) body.scope = input.scope;
  if (input.enabled !== void 0) body.enabled = input.enabled;
  if (input.priority !== void 0) body.priority = input.priority;
  if (input.sagaKind !== void 0) body.saga_kind = input.sagaKind;
  if (input.chainIds !== void 0) body.chain_ids = input.chainIds;
  if (input.spec !== void 0) body.spec = input.spec;
  return body;
}
function optionsFromWire(raw) {
  return rowsFromWire(raw).map((o) => ({
    value: asStringOr(o.value),
    label: asStringOr(o.label ?? o.value),
    description: asOptString(o.description)
  }));
}
function fieldsFromWire(raw) {
  return rowsFromWire(raw).map((f) => ({
    name: asStringOr(f.name),
    type: asStringOr(f.type, "string"),
    required: f.required === true,
    description: asOptString(f.description)
  }));
}
var LOCATION_KINDS = ["text", "integer", "flag"];
function locationsFromWire(raw) {
  return rowsFromWire(raw).map((l) => ({
    name: asStringOr(l.name),
    kind: LOCATION_KINDS.find((k) => k === l.kind) ?? "text",
    description: asOptString(l.description)
  }));
}
function scopesFromWire(raw) {
  return rowsFromWire(raw).map((o) => ({
    value: asStringOr(o.value),
    label: asStringOr(o.label ?? o.value),
    description: asOptString(o.description),
    internalOnly: o.internal_only === true,
    authorable: typeof o.authorable === "boolean" ? o.authorable : void 0
  }));
}
function catalogFromWire(raw) {
  const roles = asRecord(raw.roles);
  return {
    relationshipTypes: optionsFromWire(raw.relationship_types),
    linkageModels: optionsFromWire(raw.linkage_models),
    truthLocations: optionsFromWire(raw.truth_locations),
    transforms: optionsFromWire(raw.transforms),
    defaultTransform: asStringOr(raw.default_transform, "lower"),
    roles: {
      canonical: asStringList(roles.canonical),
      known: asStringList(roles.known),
      defaultCompleteRoles: asStringList(roles.default_complete_roles)
    },
    specFields: fieldsFromWire(raw.spec_fields),
    selectorFields: fieldsFromWire(raw.selector_fields),
    valueSourceFields: fieldsFromWire(raw.value_source_fields),
    valueLocations: locationsFromWire(raw.value_locations),
    scopes: scopesFromWire(raw.scopes),
    flow: flowCatalogFromWire(raw.flow)
  };
}
function flowCatalogFromWire(raw) {
  const flow = asRecord(raw);
  const relationshipType = asStringOr(flow.relationship_type);
  if (!relationshipType) return null;
  return {
    relationshipType,
    matches: optionsFromWire(flow.matches),
    directions: optionsFromWire(flow.directions),
    groupBys: optionsFromWire(flow.group_bys),
    fields: fieldsFromWire(flow.fields),
    nativeAsset: asStringOr(flow.native_asset, "native"),
    maxAddresses: num(flow.max_addresses, 500)
  };
}
function createApiGroupingRulesSource(client, { url = (_verb, path) => path, listQuery } = {}) {
  const invalidate = () => client.invalidateMatching("/grouping/rules");
  return {
    async specCatalog() {
      const res = await client.get(url("GET", "/grouping/spec-catalog"));
      return catalogFromWire(asRecord(unwrapData(res)));
    },
    async list() {
      return (await walkPages(client, url("GET", "/grouping/rules"), listQuery ? { query: listQuery } : {})).map(ruleFromWire);
    },
    async create(input) {
      const res = await client.post(url("ANY", "/grouping/rules"), toWire(input));
      const saved = ruleFromWire(asRecord(unwrapData(res)));
      invalidate();
      return saved;
    },
    async update(id, input) {
      const res = await client.patch(
        url("ANY", `/grouping/rules/${encodeURIComponent(id)}`),
        toWire(input)
      );
      const saved = ruleFromWire(asRecord(unwrapData(res)));
      invalidate();
      return saved;
    },
    async remove(id) {
      unwrapData(
        await client.delete(url("ANY", `/grouping/rules/${encodeURIComponent(id)}`))
      );
      invalidate();
    }
  };
}

// src/grouping/roles.ts
var CANON_SOURCE = "source";
var CANON_DESTINATION = "destination";
var SOURCE_SYNONYMS = /* @__PURE__ */ new Set(["source", "send", "origin"]);
var DESTINATION_SYNONYMS = /* @__PURE__ */ new Set(["destination", "receive", "dest"]);
function canonRole(role) {
  const value = (role ?? "").trim().toLowerCase();
  if (SOURCE_SYNONYMS.has(value)) return CANON_SOURCE;
  if (DESTINATION_SYNONYMS.has(value)) return CANON_DESTINATION;
  return value;
}

// src/grouping/sagas.ts
var SAGA_LABELS = {
  "bridge.ccip": "Chainlink CCIP",
  "bridge.cctp": "Circle CCTP",
  "bridge.wormhole": "Wormhole",
  // The rest of the bundled bridges, which fell through to the generic
  // prettify and so introduced themselves as "Bridge Layerzero V2" and
  // "Bridge Ibc" — the namespace leaked into the name and the protocol's own
  // capitalisation was lost. A card naming a protocol should spell it the way
  // the protocol does.
  //
  // Both LayerZero generations are listed and both say which: they are
  // different rails with different contracts and different chain numbering,
  // and a group card that called them both "LayerZero" would leave a reader
  // unable to tell which index to go and look in.
  "bridge.layerzero_v1": "LayerZero V1",
  "bridge.layerzero_v2": "LayerZero V2",
  "bridge.across_v3": "Across",
  "bridge.hyperlane": "Hyperlane",
  "bridge.mayan": "Mayan",
  // The one saga in this list whose key is not on the chain. A Relay fill is a
  // solver paying out of its own capital and identifies itself to nobody, so the
  // two legs are joined by the request id Relay's own API mints — carried as a
  // `CrossChainRef` with `off_chain` provenance. Nothing else here needs to know
  // that; the label is the only Relay-specific line in this package.
  "bridge.relay": "Relay",
  "bridge.hyperliquid": "Hyperliquid bridge",
  "bridge.hop": "Hop",
  "bridge.ibc": "IBC",
  "trade.token_position": "Trading position",
  "token.launch": "Token launch",
  "pool.lifecycle": "Pool lifecycle",
  // A prediction market's whole story, keyed on its `conditionId`. "Prediction
  // market" rather than "Conditional tokens" — the latter is the name of the
  // contract standard, and a reader looking at a group of bets on a question
  // should not have to know it.
  "market.lifecycle": "Prediction market",
  // The ELECTION, not one of its questions. A multi-outcome ("neg risk") market
  // is N binary conditions, each already titled "Prediction market" — naming
  // this one the same would put eleven identically-titled groups on a page and
  // leave the reader to work out that one of them is the other ten.
  "negrisk.market": "Multi-outcome market",
  "pool.rug": "Rug pull",
  "pool.jit_front_run": "JIT front-run",
  "mev.sandwich": "Sandwich",
  "launch.insider_buy": "Insider buy",
  // Custody. "Exchange transfers" rather than "Exchange deposits" because the
  // group holds both directions — the saga kind names the common case, the
  // group does not.
  "flow.exchange_deposit": "Exchange transfers",
  // One CoW order's placement and the settlement that filled it, keyed on the
  // derived `orderUid`. Prettified it read "Exchange Cowswap Order".
  "exchange.cowswap_order": "CoW Swap order",
  // NOT "Peel chain", which is the technique's name among investigators and
  // means nothing to anybody else. What the finding says is that money moved
  // through a series of wallets and stopped somewhere it can no longer be
  // followed.
  //
  // "somewhere" rather than "an exchange" because the detector's terminus is a
  // CLASS now — an exchange, a custodial service, a bridge escrow, a mixer, a
  // sanctioned address. A static label naming one of them would be wrong for
  // the other four; `peelChainLabel` below narrows it when the group's payload
  // says which.
  "flow.peel_chain": "Funds forwarded through intermediaries",
  // NOT "Dust campaign", for the same reason the peel chain is not called one:
  // "dusting" is the technique's name among investigators and says nothing to
  // the person whose wallet is full of it. What the finding says is that one
  // address sprayed worthless transfers at a great many wallets.
  "spam.dust_campaign": "Bulk unsolicited transfers",
  approve_spend: "Approve / spend",
  userop_retry: "User-op retry",
  // Every transaction touching one catalog entity — a set of wallets someone
  // labelled. The served group name is "Entity activity · <entity name>"
  // (`commons.grouping.naming`), and a commons test fails if this map and that
  // one spell a family differently.
  "entity.flow": "Entity activity"
};
function prettifyToken(value) {
  const words = value.replace(/[._-]+/g, " ").trim().split(/\s+/).filter(Boolean);
  return words.length === 0 ? value : words.map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
}
function sagaLabel(kind) {
  if (!kind) return "Cross-chain";
  return SAGA_LABELS[kind] ?? prettifyToken(kind);
}

// src/grouping-rules/standing.ts
function flowSummary(spec2) {
  const flow = spec2?.flow;
  if (!flow || typeof flow !== "object") return null;
  const f = flow;
  const count = Array.isArray(f.addresses) ? f.addresses.length : 0;
  const noun = `${count} address${count === 1 ? "" : "es"}`;
  const touching = f.match === "touching";
  const direction = f.direction === "outgoing" ? "Sent from " : f.direction === "incoming" ? "Received by " : "";
  const counterparties = Array.isArray(f.counterparties) ? f.counterparties.length : 0;
  const head = touching ? `${direction || "Touching "}${noun}${counterparties ? ` \xB7 with ${counterparties} counterpart${counterparties === 1 ? "y" : "ies"}` : ""}` : `Between ${noun}`;
  const groups = {
    rule: "one group",
    address: "a group per address",
    pair: "a group per pair",
    counterparty: "a group per counterparty"
  };
  const assets = Array.isArray(f.assets) && f.assets.length ? ` \xB7 ${f.assets.length} asset${f.assets.length === 1 ? "" : "s"}` : "";
  return `${head} \xB7 ${groups[String(f.group_by ?? "rule")] ?? "one group"}${assets}`;
}
function standingOf(rule) {
  return rule.standing?.state ?? "unknown";
}
function neverMatchedNote(rule) {
  const standing = rule.standing;
  switch (standing?.state) {
    case "shadowed":
      return { warn: false, title: "Never matched \u2014 expected: a more specific rule wins for this saga kind, so this one never applies." };
    case "disabled":
      return { warn: false, title: "Never matched \u2014 expected: the rule is turned off." };
    case "invalid":
      return { warn: false, title: "Never matched \u2014 the engine cannot read this rule's spec, so it never competes. Fix the spec." };
    case "not_applicable":
      return { warn: false, title: "Never matched in your runs \u2014 expected: this is another user's private rule." };
    case "in_effect":
      return {
        warn: true,
        title: standing.chainIds.length > 0 ? `This rule has never been credited with a group. It applies only on ${standing.chainIds.join(", ")}, so transactions anywhere else cannot match it however well the spec fits. Otherwise nothing has matched it yet.` : "This rule has never been credited with a group \u2014 nothing it selects has been seen by a run yet, or its selectors do not match what the protocol emits."
      };
    default:
      return { warn: false, title: "Never matched. Whether this rule should have applied could not be determined." };
  }
}
function deletionImpact(rule, rules) {
  const standing = rule.standing;
  const kind = standing?.sagaKind || rule.sagaKind || "";
  if (!standing) return { kind, known: false, wasInEffect: false, successors: [] };
  const byId = new Map(rules.map((r) => [r.id, r]));
  const successors = standing.takesOverIfDeleted.map((id) => byId.get(id)).filter((r) => r !== void 0);
  return { kind, known: true, wasInEffect: standing.state === "in_effect", successors };
}
function groupBySagaKind(rules) {
  const buckets = /* @__PURE__ */ new Map();
  for (const rule of rules) {
    const kind = rule.standing?.sagaKind || rule.sagaKind || "";
    const list = buckets.get(kind) ?? [];
    list.push(rule);
    buckets.set(kind, list);
  }
  const byLadder = (a, b) => {
    const left = a.standing?.position;
    const right = b.standing?.position;
    if (left === void 0 || right === void 0) {
      return left === right ? 0 : left === void 0 ? 1 : -1;
    }
    return left - right;
  };
  return Array.from(buckets.entries()).map(([kind, list]) => ({ kind, rules: list.slice().sort(byLadder) })).sort((a, b) => sagaLabel(a.kind).localeCompare(sagaLabel(b.kind)));
}

// src/grouping-rules/ruleForm.ts
function scopeOptions(scopes, canAuthorSystem, current, allowed) {
  const offered = scopes.filter(
    (scope) => (
      // The API's answer for this caller when it sends one; the host's guess
      // otherwise. The dashboard's guess (`pipelines:admin`) offered `system`
      // to callers the API then refused — it wants an internal caller.
      (scope.authorable ?? (canAuthorSystem || !scope.internalOnly)) && (!allowed || allowed.includes(scope.value))
    )
  );
  if (current && !offered.some((scope) => scope.value === current)) {
    const known = scopes.find((scope) => scope.value === current);
    offered.push(known ?? { value: current, label: current, internalOnly: false });
  }
  return offered;
}
function requiredSpecFields(catalog) {
  return (catalog?.specFields ?? []).filter((f) => f.required).map((f) => f.name);
}
function parseSpec(text, required = []) {
  const trimmed = text.trim();
  if (!trimmed) return { error: "A rule with no spec never matches anything." };
  let parsed;
  try {
    parsed = JSON.parse(trimmed);
  } catch (err) {
    return { error: `Not valid JSON: ${err instanceof Error ? err.message : String(err)}` };
  }
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    return { error: "The spec must be a JSON object." };
  }
  const spec2 = parsed;
  const missing = required.filter((field) => {
    const value = spec2[field];
    return value === void 0 || value === null || value === "";
  });
  if (missing.length > 0) {
    return {
      error: `Missing required field${missing.length === 1 ? "" : "s"}: ${missing.join(", ")}. The engine would skip this rule.`
    };
  }
  return { spec: spec2 };
}
function validate(values, required = []) {
  const errors = {};
  if (!values.name.trim()) errors.name = "Give the rule a name you will recognise in the list.";
  if (!Number.isInteger(values.priority) || values.priority < 0) {
    errors.priority = "Priority is a whole number; lower wins within a scope.";
  }
  const { error } = parseSpec(values.specText, required);
  if (error) errors.specText = error;
  return errors;
}
function isValid(values, required = []) {
  return Object.keys(validate(values, required)).length === 0;
}
function toCreateInput(values) {
  const { spec: spec2 } = parseSpec(values.specText);
  const sagaKind = String(spec2?.saga_kind ?? "").trim() || values.sagaKind.trim() || null;
  return {
    name: values.name.trim(),
    description: values.description.trim() || null,
    scope: values.scope,
    enabled: values.enabled,
    priority: values.priority,
    sagaKind,
    chainIds: values.chainIds,
    spec: spec2
  };
}
function toUpdateInput(values) {
  const input = toCreateInput(values);
  const { scope: _scope, ...rest } = input;
  return rest;
}

// src/chains/family.ts
var ChainFamily = {
  EVM: "evm",
  UTXO: "utxo",
  SOLANA: "solana",
  COSMOS: "cosmos",
  /** Hyperliquid's L1 (HyperCore): one signed action per transaction, EVM-shaped identifiers. */
  HYPERCORE: "hypercore",
  OTHER: "other"
};
var EVM_IDENTIFIER_FAMILIES = /* @__PURE__ */ new Set([
  ChainFamily.EVM,
  ChainFamily.HYPERCORE
]);
function hasEvmIdentifiers(family) {
  return family != null && EVM_IDENTIFIER_FAMILIES.has(family);
}

// src/chains/seed-data.ts
var CHAIN_MAPPING = {
  // --- EVM mainnets ---
  "0x1": "eth",
  "0x3d": "etc",
  "0x89": "polygon",
  "0x38": "bsc",
  "0xcc": "opbnb",
  "0xa86a": "avalanche",
  "0xfa": "fantom",
  "0x19": "cronos",
  "0x2a15c308d": "palm",
  "0xa4b1": "arbitrum",
  "0x64": "gnosis",
  "0x15b38": "chiliz",
  "0x2105": "base",
  "0xa": "optimism",
  "0x2019": "klaytn",
  "0xa4ec": "celo",
  "0x8ae": "kava",
  "0x440": "metis",
  "0x63564c40": "harmony",
  "0x171": "pulsechain",
  "0x1388": "mantle",
  // Nesa. A Cosmos-SDK chain with an EVM module, so its Cosmos chain id
  // (`nesa_41443-1`) and its EVM chain id are the same 41443. Ported from
  // `_seed_data.py`, which has carried it since the Hyperlane domain fallback
  // was retired — without this row every spelling of Nesa falls through the
  // registry unresolved and renders as a raw `eip155:41443`.
  "0xa1e3": "nesa",
  "0x505": "moonriver",
  "0x504": "moonbeam",
  "0x144": "zkSync_era",
  "0x44d": "polygon_zkevm",
  "0xe708": "linea",
  "0x82750": "scroll",
  "0x13e31": "blast",
  "0x4e454152": "aurora",
  "0x7a": "fuse",
  "0x2eb": "flow",
  "0x7e4": "ronin",
  "0x46f": "lisk",
  "0x1b58": "zetachain",
  "0x531": "sei",
  // HyperEVM. Its L1 order book (HyperCore) is a non-EVM chain in registry.ts.
  "0x3e7": "hyperevm",
  // Ink, Kraken's OP Stack L2 — ported from `_seed_data.py` (CCIP + LayerZero route to it).
  "0xdef1": "ink",
  // Robinhood Chain (4663). Seeded alongside Ink because a Relay transfer
  // has a leg on each and, unseeded, the id resolved to nothing at all.
  "0x1237": "robinhood",
  "0x8f": "monad",
  "0x2611": "plasma",
  // peaq. Ported from `_seed_data.py`, where it was seeded because a LayerZero
  // OFT leg named its endpoint id and the chip rendered the bare number. The
  // lower-case name is the project's own styling.
  "0xd0a": "peaq",
  // GRVT, a ZK Stack chain. This id was seeded as `zkSync_era_testnet`, so
  // `eip155:325` resolved as a zkSync Era testnet paired with Era mainnet; Era's
  // testnets are 280 and 300. Ported from `_seed_data.py`, which carries the
  // sources for the id and for the native token (GBT, not ETH).
  "0x145": "grvt",
  // --- EVM testnets ---
  "0x5": "goerli",
  "0xaa36a7": "sepolia",
  "0x4268": "holesky",
  "0x13881": "mumbai",
  "0x13882": "polygon_amoy",
  "0x61": "bsc_testnet",
  "0x27d8": "gnosis_chiado",
  "0x15b32": "chiliz_spicy",
  "0x14a33": "base_goerli",
  "0x14a34": "base_sepolia",
  "0x1a4": "optimism_goerli",
  "0xaa37dc": "optimism_sepolia",
  "0x66eee": "arbitrum_sepolia",
  "0x3e9": "klaytn_baobab",
  "0xaef3": "celo_alfajores",
  "0x8ad": "kava_testnet",
  "0x24c": "metis_goerli",
  "0x6357d2e0": "harmony_testnet",
  "0x4e454153": "aurora_testnet",
  "0x507": "moonbase",
  "0x118": "zkSync_testnet",
  "0x12c": "zksync_sepolia",
  "0x7b": "fuse_testnet",
  "0xe704": "linea_testnet",
  "0xe705": "linea_sepolia",
  "0x8274f": "scroll_testnet",
  "0x138b": "mantle_sepolia",
  "0xa0c71fd": "blast_sepolia",
  "0x98a": "polygon_zkevm_cardona",
  "0xfa2": "fantom_testnet",
  "0x106a": "lisk_sepolia",
  "0x221": "flow_testnet",
  "0x7e5": "ronin_saigon",
  "0x1b59": "zetachain_testnet",
  "0x530": "sei_testnet",
  "0x3e6": "hyperevm_testnet"
};
var TESTNET_CHAINS = [
  "goerli",
  "sepolia",
  "mumbai",
  "bsc_testnet",
  "gnosis_chiado",
  "chiliz_spicy",
  "base_goerli",
  "optimism_goerli",
  "klaytn_baobab",
  "celo_alfajores",
  "kava_testnet",
  "metis_goerli",
  "harmony_testnet",
  "zkSync_testnet",
  "aurora_testnet",
  "moonbase",
  "fuse_testnet",
  "linea_testnet",
  "scroll_testnet",
  "holesky",
  "polygon_amoy",
  "base_sepolia",
  "optimism_sepolia",
  "arbitrum_sepolia",
  "linea_sepolia",
  "blast_sepolia",
  "mantle_sepolia",
  "zksync_sepolia",
  "polygon_zkevm_cardona",
  "fantom_testnet",
  "lisk_sepolia",
  "flow_testnet",
  "ronin_saigon",
  "zetachain_testnet",
  "sei_testnet",
  "hyperevm_testnet"
];
var LAYER1_CHAINS = [
  "eth",
  "etc",
  "bsc",
  "avalanche",
  "fantom",
  "klaytn",
  "celo",
  "kava",
  "harmony",
  "moonriver",
  "moonbeam",
  "fuse",
  "pulsechain",
  "flow",
  "ronin",
  "sei",
  "monad",
  "peaq",
  "zetachain",
  "hyperevm"
];
var LAYER2_CHAINS = [
  "polygon",
  "arbitrum",
  "optimism",
  "metis",
  "base",
  "zkSync_era",
  "linea",
  "scroll",
  "mantle",
  "opbnb",
  "polygon_zkevm",
  "blast",
  "lisk",
  "grvt",
  "ink",
  "robinhood"
];
var SIDECHAINS = ["palm", "gnosis", "cronos", "aurora"];
var CHAIN_CONFIG = {
  "0x1": { token: "ETH", gasUnit: "Gwei", name: "Ethereum" },
  "0x3d": { token: "ETC", gasUnit: "Gwei", name: "Ethereum Classic" },
  "0x89": { token: "MATIC", gasUnit: "Gwei", name: "Polygon" },
  "0x38": { token: "BNB", gasUnit: "Gwei", name: "BNB Chain" },
  // Gas on Arbitrum is ETH; ARB is a governance token.
  "0xa4b1": { token: "ETH", gasUnit: "Gwei", name: "Arbitrum" },
  "0x2105": { token: "ETH", gasUnit: "Gwei", name: "Base" },
  "0xa": { token: "ETH", gasUnit: "Gwei", name: "Optimism" },
  "0xe708": { token: "ETH", gasUnit: "Gwei", name: "Linea" },
  "0xa86a": { token: "AVAX", gasUnit: "nAVAX", name: "Avalanche" },
  "0xfa": { token: "FTM", gasUnit: "Gwei", name: "Fantom" },
  "0x19": { token: "CRO", gasUnit: "Gwei", name: "Cronos" },
  "0x64": { token: "xDAI", gasUnit: "Gwei", name: "Gnosis" },
  "0x15b38": { token: "CHZ", gasUnit: "Gwei", name: "Chiliz" },
  "0x171": { token: "PLS", gasUnit: "Gwei", name: "PulseChain" },
  "0x504": { token: "GLMR", gasUnit: "Gwei", name: "Moonbeam" },
  "0x505": { token: "MOVR", gasUnit: "Gwei", name: "Moonriver" },
  "0x13e31": { token: "ETH", gasUnit: "Gwei", name: "Blast" },
  "0x144": { token: "ETH", gasUnit: "Gwei", name: "zkSync Era" },
  "0x1388": { token: "MNT", gasUnit: "Gwei", name: "Mantle" },
  "0xa1e3": { token: "NES", gasUnit: "Gwei", name: "Nesa" },
  "0xd0a": { token: "PEAQ", gasUnit: "Gwei", name: "peaq" },
  // GRVT pays gas in GBT (its ZKsync Bridgehub base token), not ETH.
  "0x145": { token: "GBT", gasUnit: "Gwei", name: "GRVT" },
  "0xcc": { token: "BNB", gasUnit: "Gwei", name: "opBNB" },
  // Gas on Polygon zkEVM is ETH, not MATIC.
  "0x44d": { token: "ETH", gasUnit: "Gwei", name: "Polygon zkEVM" },
  "0x1b58": { token: "ZETA", gasUnit: "Gwei", name: "ZetaChain" },
  "0x82750": { token: "ETH", gasUnit: "Gwei", name: "Scroll" },
  "0x4e454152": { token: "ETH", gasUnit: "Gwei", name: "Aurora" },
  "0x531": { token: "SEI", gasUnit: "Gwei", name: "Sei" },
  "0x3e7": { token: "HYPE", gasUnit: "Gwei", name: "HyperEVM" },
  "0xdef1": { token: "ETH", gasUnit: "Gwei", name: "Ink" },
  "0x1237": { token: "ETH", gasUnit: "Gwei", name: "Robinhood Chain" },
  "0x3e6": { token: "HYPE", gasUnit: "Gwei", name: "HyperEVM Testnet" },
  "0x8f": { token: "MON", gasUnit: "Gwei", name: "Monad" },
  "0x3e9": { token: "KLAY", gasUnit: "Gwei", name: "Klaytn Baobab" },
  "0x4e454153": { token: "ETH", gasUnit: "Gwei", name: "Aurora Testnet" },
  "0x8274f": { token: "ETH", gasUnit: "Gwei", name: "Scroll Sepolia" },
  "0x530": { token: "SEI", gasUnit: "Gwei", name: "Sei Testnet" },
  "0x2eb": { token: "FLOW", gasUnit: "FLOW units", name: "Flow" },
  "0x7e4": { token: "RON", gasUnit: "Gwei", name: "Ronin" },
  "0x46f": { token: "LSK", gasUnit: "LSK units", name: "Lisk" },
  "0xaa36a7": { token: "ETH", gasUnit: "Gwei", name: "Sepolia" },
  "0x4268": { token: "ETH", gasUnit: "Gwei", name: "Holesky" },
  "0x13882": { token: "MATIC", gasUnit: "Gwei", name: "Polygon Amoy" },
  "0x61": { token: "tBNB", gasUnit: "Gwei", name: "BSC Testnet" },
  "0x66eee": { token: "ETH", gasUnit: "Gwei", name: "Arbitrum Sepolia" },
  "0x14a34": { token: "ETH", gasUnit: "Gwei", name: "Base Sepolia" },
  "0xaa37dc": { token: "ETH", gasUnit: "Gwei", name: "Optimism Sepolia" },
  "0xe705": { token: "ETH", gasUnit: "Gwei", name: "Linea Sepolia" },
  "0xfa2": { token: "FTM", gasUnit: "Gwei", name: "Fantom Testnet" },
  "0x27d8": { token: "xDAI", gasUnit: "Gwei", name: "Gnosis Chiado" },
  "0x15b32": { token: "CHZ", gasUnit: "Gwei", name: "Chiliz Testnet" },
  "0x507": { token: "DEV", gasUnit: "Gwei", name: "Moonbase" },
  "0xa0c71fd": { token: "ETH", gasUnit: "Gwei", name: "Blast Sepolia" },
  "0x12c": { token: "ETH", gasUnit: "Gwei", name: "zkSync Sepolia" },
  "0x138b": { token: "MNT", gasUnit: "Gwei", name: "Mantle Sepolia" },
  "0x98a": { token: "MATIC", gasUnit: "Gwei", name: "Polygon zkEVM Cardona" },
  "0x1b59": { token: "ZETA", gasUnit: "Gwei", name: "Zetachain Testnet" },
  "0x221": { token: "FLOW", gasUnit: "FLOW units", name: "Flow Testnet" },
  "0x7e5": { token: "RON", gasUnit: "Gwei", name: "Ronin Saigon Testnet" },
  "0x106a": { token: "LSK", gasUnit: "LSK units", name: "Lisk Sepolia" },
  "0x0": { token: "BTC", gasUnit: "Sats/byte", name: "Bitcoin" },
  "0x1399c": { token: "SOL", gasUnit: "Lamports", name: "Solana" },
  "0x162": { token: "DOT", gasUnit: "Plancks", name: "Polkadot" },
  "0x2611": { token: "XPL", gasUnit: "Gwei", name: "Plasma" },
  // Without a row, `seedFromCanonical` names a chain by its slug ("celo") and
  // gives it ETH. Every seeded chain has one; the rows below were added in
  // `_seed_data.py` first and ported, and the parity test keeps them in step.
  "0xa4ec": { token: "CELO", gasUnit: "Gwei", name: "Celo" },
  "0x440": { token: "METIS", gasUnit: "Gwei", name: "Metis" },
  "0x8ae": { token: "KAVA", gasUnit: "Gwei", name: "Kava" },
  "0x63564c40": { token: "ONE", gasUnit: "Gwei", name: "Harmony" },
  "0x7a": { token: "FUSE", gasUnit: "Gwei", name: "Fuse" },
  "0x2019": { token: "KLAY", gasUnit: "Gwei", name: "Klaytn" },
  "0x2a15c308d": { token: "PALM", gasUnit: "Gwei", name: "Palm" },
  "0x5": { token: "ETH", gasUnit: "Gwei", name: "Goerli" },
  "0x13881": { token: "MATIC", gasUnit: "Gwei", name: "Polygon Mumbai" },
  "0x14a33": { token: "ETH", gasUnit: "Gwei", name: "Base Goerli" },
  "0x1a4": { token: "ETH", gasUnit: "Gwei", name: "Optimism Goerli" },
  "0xaef3": { token: "CELO", gasUnit: "Gwei", name: "Celo Alfajores" },
  "0x8ad": { token: "TKAVA", gasUnit: "Gwei", name: "Kava Testnet" },
  "0x24c": { token: "METIS", gasUnit: "Gwei", name: "Metis Goerli" },
  "0x6357d2e0": { token: "ONE", gasUnit: "Gwei", name: "Harmony Testnet" },
  "0x118": { token: "ETH", gasUnit: "Gwei", name: "zkSync Goerli" },
  "0x7b": { token: "SPARK", gasUnit: "Gwei", name: "Fuse Sparknet" },
  "0xe704": { token: "ETH", gasUnit: "Gwei", name: "Linea Goerli" }
};
var EXPLORER_URLS = {
  eth: "https://etherscan.io",
  etc: "https://blockscout.com/etc/mainnet",
  polygon: "https://polygonscan.com",
  bsc: "https://bscscan.com",
  avalanche: "https://snowtrace.io",
  fantom: "https://ftmscan.com",
  arbitrum: "https://arbiscan.io",
  optimism: "https://optimistic.etherscan.io",
  zksync_era: "https://explorer.zksync.io",
  base: "https://basescan.org",
  linea: "https://lineascan.build",
  scroll: "https://scrollscan.com",
  mantle: "https://explorer.mantle.xyz",
  // The EVM-side Blockscout. Nesa also runs a Cosmos explorer at
  // `explorer.nesa.ai`, which cannot resolve an `0x` address or tx hash.
  nesa: "https://explorer-evm.nesa.ai",
  // Subscan, whose EVM mode answers the default /tx, /address, /block and
  // /token paths (each checked against a live peaq tx on 2026-09-16).
  peaq: "https://peaq.subscan.io",
  opbnb: "https://opbnbscan.com",
  polygon_zkevm: "https://zkevm.polygonscan.com",
  blast: "https://blastscan.io",
  pulsechain: "https://scan.pulsechain.com",
  aurora: "https://explorer.aurora.dev",
  flow: "https://evm.flowscan.io",
  ronin: "https://app.roninchain.com",
  lisk: "https://blockscout.lisk.com",
  zetachain: "https://explorer.zetachain.com",
  sei: "https://seitrace.com",
  hyperevm: "https://hyperevmscan.io",
  ink: "https://explorer.inkonchain.com",
  robinhood: "https://robinhoodchain.blockscout.com",
  monad: "https://monadexplorer.com",
  plasma: "https://plasmascan.to",
  // --- Testnets ---
  // A testnet with no row here gets NO explorer link — the registry does not
  // lend it its mainnet's, which would show a different chain's object under
  // the same hash or address. So a row belongs here only when the testnet has
  // its own explorer that is live; these were checked on 2026-09-16 (DNS, the
  // site answering, and the chain id where the explorer exposes one).
  //
  // Deliberately absent, because the explorer is gone or now serves another
  // network: holesky (holesky.etherscan.io no longer resolves), mumbai
  // (mumbai.polygonscan.com no longer resolves — the Python table still carries
  // it), base_goerli, optimism_goerli, linea_testnet, zkSync_testnet,
  // metis_goerli, polygon_zkevm_cardona, fantom_testnet, celo_alfajores (both
  // its explorers redirect to Celo Sepolia), scroll_testnet
  // (sepolia.scrollscan.com is a Cloudflare 1014), lisk_sepolia (unreachable)
  // and sei_testnet (Seitrace picks the network by query string, which the link
  // builder cannot express).
  //
  // FE-only: the Python table has no mainnet fallback to correct, and feeds
  // scraping hosts, so these are not mirrored there.
  goerli: "https://goerli.etherscan.io",
  sepolia: "https://sepolia.etherscan.io",
  polygon_amoy: "https://amoy.polygonscan.com",
  bsc_testnet: "https://testnet.bscscan.com",
  gnosis_chiado: "https://gnosis-chiado.blockscout.com",
  base_sepolia: "https://sepolia.basescan.org",
  optimism_sepolia: "https://sepolia-optimism.etherscan.io",
  arbitrum_sepolia: "https://sepolia.arbiscan.io",
  aurora_testnet: "https://explorer.testnet.aurora.dev",
  moonbase: "https://moonbase.moonscan.io",
  fuse_testnet: "https://explorer.fusespark.io",
  linea_sepolia: "https://sepolia.lineascan.build",
  blast_sepolia: "https://sepolia.blastscan.io",
  mantle_sepolia: "https://sepolia.mantlescan.xyz",
  zksync_sepolia: "https://sepolia.explorer.zksync.io",
  flow_testnet: "https://testnet.evm.flow.com",
  ronin_saigon: "https://saigon-app.roninchain.com",
  zetachain_testnet: "https://testnet.zetascan.com"
};
var TESTNET_TO_MAINNET = {
  goerli: "eth",
  sepolia: "eth",
  mumbai: "polygon",
  bsc_testnet: "bsc",
  gnosis_chiado: "gnosis",
  chiliz_spicy: "chiliz",
  base_goerli: "base",
  optimism_goerli: "optimism",
  klaytn_baobab: "klaytn",
  celo_alfajores: "celo",
  kava_testnet: "kava",
  metis_goerli: "metis",
  harmony_testnet: "harmony",
  zkSync_testnet: "zkSync_era",
  aurora_testnet: "aurora",
  moonbase: "moonbeam",
  fuse_testnet: "fuse",
  linea_testnet: "linea",
  scroll_testnet: "scroll",
  holesky: "eth",
  polygon_amoy: "polygon",
  base_sepolia: "base",
  optimism_sepolia: "optimism",
  arbitrum_sepolia: "arbitrum",
  linea_sepolia: "linea",
  blast_sepolia: "blast",
  mantle_sepolia: "mantle",
  zksync_sepolia: "zkSync_era",
  polygon_zkevm_cardona: "polygon_zkevm",
  fantom_testnet: "fantom",
  lisk_sepolia: "lisk",
  flow_testnet: "flow",
  ronin_saigon: "ronin",
  zetachain_testnet: "zetachain",
  sei_testnet: "sei",
  hyperevm_testnet: "hyperevm"
};
var CHAIN_ALIASES = {
  "eth": ["ethereum", "ethereum-mainnet", "eth-mainnet"],
  "polygon": ["matic", "matic-network", "polygon-mainnet", "polygon-pos"],
  "bsc": ["binance", "binance-smart-chain", "bnb", "bnb-chain", "bnb-smart-chain"],
  "avalanche": ["avax", "avalanche-c-chain", "avax-c-chain", "avalanche-mainnet"],
  "arbitrum": ["arbitrum-one", "arb", "arbitrum-mainnet"],
  "optimism": ["op", "op-mainnet", "optimism-mainnet"],
  "base": ["base-mainnet"],
  "gnosis": ["xdai", "gnosis-chain"],
  "fantom": ["ftm", "fantom-opera"],
  "cronos": ["cro"],
  "linea": ["linea-mainnet"],
  "scroll": ["scroll-mainnet"],
  "blast": ["blast-mainnet"],
  "ronin": ["ron"],
  "sei": ["sei-mainnet"],
  "pulsechain": ["pls"],
  "polygon_zkevm": ["polygon-zkevm", "zkevm"],
  "zksync_era": ["zksync", "zksync-era", "era"],
  "mantle_sepolia": ["mantle-testnet", "mantle_testnet"],
  // Non-EVM — the short tickers callers reach for.
  "solana": ["sol"],
  "bitcoin": ["btc"],
  "polkadot": ["dot"],
  "hypercore": ["hyperliquid", "hyperliquid-core", "hyperliquid-l1"],
  "hyperevm": ["hyper-evm", "hyperliquid-evm"],
  "hypercore-testnet": ["hyperliquid-testnet", "hypercore_testnet"],
  "hyperevm_testnet": ["hyperevm-testnet", "hyperliquid-evm-testnet"],
  "monero": ["xmr"],
  "zcash": ["zec"]
};

// src/chains/registry.ts
function foldAliases(...groups) {
  const seen = /* @__PURE__ */ new Set();
  for (const group of groups) {
    for (const raw of group ?? []) {
      const alias = raw.trim().toLowerCase();
      if (alias) seen.add(alias);
    }
  }
  return [...seen];
}
var ChainRegistry = class {
  bySlug = /* @__PURE__ */ new Map();
  byHex = /* @__PURE__ */ new Map();
  byCaip2 = /* @__PURE__ */ new Map();
  byAlias = /* @__PURE__ */ new Map();
  register(chain, { replace = false } = {}) {
    const existingSlug = this.bySlug.get(chain.slug);
    if (existingSlug && existingSlug !== chain && !replace) {
      throw new Error(
        `Chain slug "${chain.slug}" already registered; pass replace=true to overwrite.`
      );
    }
    if (chain.chainIdHex) {
      const existingHex = this.byHex.get(chain.chainIdHex);
      if (existingHex && existingHex.slug !== chain.slug && !replace) {
        throw new Error(
          `Chain hex ${chain.chainIdHex} already mapped to slug ${existingHex.slug}; pass replace=true to overwrite.`
        );
      }
    }
    if (existingSlug) this.dropAliases(existingSlug);
    this.bySlug.set(chain.slug, chain);
    if (chain.chainIdHex) this.byHex.set(chain.chainIdHex, chain);
    if (chain.caip2) this.byCaip2.set(chain.caip2, chain);
    for (const alias of foldAliases(chain.aliases)) this.byAlias.set(alias, chain);
  }
  dropAliases(chain) {
    for (const alias of foldAliases(chain.aliases)) {
      if (this.byAlias.get(alias) === chain) this.byAlias.delete(alias);
    }
  }
  unregister(slug) {
    const chain = this.bySlug.get(slug);
    if (!chain) return null;
    this.bySlug.delete(slug);
    if (chain.chainIdHex) this.byHex.delete(chain.chainIdHex);
    if (chain.caip2) this.byCaip2.delete(chain.caip2);
    this.dropAliases(chain);
    return chain;
  }
  clear() {
    this.bySlug.clear();
    this.byHex.clear();
    this.byCaip2.clear();
    this.byAlias.clear();
  }
  get(slug) {
    return this.bySlug.get(slug) ?? null;
  }
  /** Lookup by EVM hex chain id (auto `0x`-prefixes + lowercases). */
  getByHex(chainIdHex) {
    let normalized = chainIdHex.toLowerCase();
    if (!normalized.startsWith("0x")) normalized = "0x" + normalized.replace(/^0x/, "");
    return this.byHex.get(normalized) ?? null;
  }
  /** Lookup by CAIP-2 chain id (e.g. "eip155:1"). */
  getByCaip2(caip2) {
    return this.byCaip2.get(normalizeCaip2(caip2)) ?? null;
  }
  /**
   * Resolve by CAIP-2, hex, bare decimal, slug, or alias — in that order, the
   * same order as `ChainRegistry.resolve` in `commons/chains/registry.py`, so a
   * spelling resolves to the same chain on both sides of the wire.
   */
  resolve(value) {
    if (!value) return null;
    if (value.includes(":")) {
      const hit = this.getByCaip2(value);
      if (hit) return hit;
    }
    if (value.startsWith("0x")) {
      const hit = this.getByHex(value);
      if (hit) return hit;
    }
    if (/^\d+$/.test(value)) {
      const hit = this.getByHex("0x" + BigInt(value).toString(16));
      if (hit) return hit;
    }
    const folded = value.toLowerCase();
    return this.get(folded) ?? this.byAlias.get(folded) ?? null;
  }
  byFamily(family) {
    return [...this.bySlug.values()].filter((c) => c.family === family).sort((a, b) => a.slug.localeCompare(b.slug));
  }
  all() {
    return [...this.bySlug.values()].sort((a, b) => a.slug.localeCompare(b.slug));
  }
};
function normalizeCaip2(value) {
  const split = value.trim().indexOf(":");
  if (split < 0) return value.trim();
  const namespace = value.trim().slice(0, split).toLowerCase();
  let reference = value.trim().slice(split + 1);
  if (["bip122", "polkadot"].includes(namespace)) reference = reference.toLowerCase();
  if (namespace === "eip155" && /^\d+$/.test(reference)) reference = BigInt(reference).toString();
  return `${namespace}:${reference}`;
}
globalSingleton("chains/explorers", () => ({
  overrides: /* @__PURE__ */ new Map(),
  pathDefaults: {}
}));
var REORG_DEPTHS_BY_SLUG = {
  eth: 12,
  polygon: 256,
  matic: 256,
  bsc: 15,
  arbitrum: 12,
  optimism: 12,
  base: 12,
  avalanche: 12,
  fantom: 12,
  cronos: 12,
  gnosis: 12,
  linea: 12,
  scroll: 12,
  mantle: 12,
  zksync: 12,
  zksync_era: 12,
  metis: 12,
  celo: 12,
  klaytn: 12,
  moonbeam: 12,
  moonriver: 12,
  harmony: 12,
  aurora: 12,
  fuse: 12,
  palm: 12,
  chiliz: 12,
  kava: 12,
  blast: 12,
  bitcoin: 6,
  solana: 32,
  // Monero's ten-block spend lock is a network rule; Zcash needs many more
  // blocks than Bitcoin for equal work at a 75-second block.
  monero: 10,
  zcash: 24,
  // HyperBFT commits a block final.
  hypercore: 1,
  hyperevm: 1,
  hyperevm_testnet: 1
};
var BLOCK_TIME_BY_SLUG = {
  eth: 12,
  etc: 13,
  polygon: 2,
  matic: 2,
  bsc: 3,
  arbitrum: 0.25,
  optimism: 2,
  base: 2,
  avalanche: 2,
  fantom: 1,
  cronos: 6,
  gnosis: 5,
  linea: 3,
  scroll: 3,
  mantle: 2,
  zksync: 1,
  zksync_era: 1,
  metis: 4,
  celo: 5,
  klaytn: 1,
  moonbeam: 12,
  moonriver: 12,
  harmony: 2,
  aurora: 1,
  fuse: 5,
  blast: 2,
  bitcoin: 600,
  solana: 0.4,
  polkadot: 6,
  monero: 120,
  zcash: 75,
  hypercore: 0.2,
  "hypercore-testnet": 0.2,
  hyperevm: 1
};
var EXPLORER_SUPPLEMENT = {
  bitcoin: "https://blockstream.info",
  solana: "https://solscan.io",
  polkadot: "https://polkadot.subscan.io",
  monero: "https://xmrchain.net",
  // Blockchair's base carries the coin as a path segment, which is exactly
  // what the link builder appends to — `/zcash` + `/transaction/<hash>`.
  zcash: "https://blockchair.com/zcash",
  // app.hyperliquid.xyz/explorer/tx/<hash>, /address/<addr>, /block/<n>.
  hypercore: "https://app.hyperliquid.xyz/explorer",
  "hypercore-testnet": "https://app.hyperliquid-testnet.xyz/explorer",
  cronos: "https://cronoscan.com",
  gnosis: "https://gnosisscan.io",
  celo: "https://celoscan.io",
  moonbeam: "https://moonscan.io",
  zkSync_era: "https://explorer.zksync.io",
  matic: "https://polygonscan.com"
};
function explorerFor(slug, rawSlug) {
  return EXPLORER_URLS[slug] ?? EXPLORER_URLS[rawSlug] ?? EXPLORER_SUPPLEMENT[slug] ?? EXPLORER_SUPPLEMENT[rawSlug] ?? null;
}
var BRAND_COLOR_BY_SLUG = {
  eth: "#627EEA",
  etc: "#328332",
  polygon: "#8247E5",
  matic: "#8247E5",
  bsc: "#F0B90B",
  arbitrum: "#28A0F0",
  optimism: "#FF0420",
  base: "#0052FF",
  avalanche: "#E84142",
  fantom: "#1969FF",
  cronos: "#002D74",
  gnosis: "#0B6E4F",
  linea: "#61DFFF",
  scroll: "#EBC28E",
  mantle: "#000000",
  zksync: "#8C8DFC",
  zksync_era: "#8C8DFC",
  metis: "#00DACC",
  celo: "#FCFF52",
  klaytn: "#FF1B1B",
  moonbeam: "#53CBC8",
  moonriver: "#C0C0C0",
  harmony: "#00AEE9",
  aurora: "#70D44B",
  fuse: "#B4F9BA",
  blast: "#FCFC03",
  kava: "#FF433E",
  bitcoin: "#F7931A",
  solana: "#14F195",
  polkadot: "#E6007A",
  monero: "#FF6600",
  zcash: "#F4B728",
  hypercore: "#97FCE4",
  hyperevm: "#97FCE4"
};
function brandColorFor(slug, mainnetSlug) {
  return BRAND_COLOR_BY_SLUG[slug] ?? (mainnetSlug ? BRAND_COLOR_BY_SLUG[mainnetSlug] ?? null : null);
}
var LOGO_URL_BY_SLUG = {};
var CAIP2_BY_SLUG = {
  "bitcoin-testnet": "bip122:000000000933ea01ad0ee984209779ba",
  "solana-devnet": "solana:EtWTRABZaYq6iMfeYKouRu166VU2xqa1",
  "solana-testnet": "solana:4uhcVJyU9pJkvQyS88uRDiswHXSCkY3z",
  bitcoin: "bip122:000000000019d6689c085ae165831e93",
  solana: "solana:5eykt4UsFv8P8NJdTREpY1vzqKqZKvdp",
  polkadot: "polkadot:91b171bb158e2d3848fa23a9f1c25182",
  // Zcash is a Bitcoin fork, so `bip122` is its registered namespace and the
  // reference is its genesis hash truncated to 32 chars — Bitcoin's own rule.
  zcash: "bip122:00040fe8ec8471911baa1db1266ea15d",
  // Monero has no CASA-registered namespace; `monero` is a platform-local
  // choice. See the note on the Python side, which this table must match
  // byte for byte — `test_chains_fe_registry_parity.py` fails if it drifts.
  monero: "monero:418015bb9ae982a1975da7d79277c270",
  // HyperCore has no CASA namespace, and `hyperliquid` / `hypercore` exceed
  // CAIP-2's eight-character namespace limit. Platform-local; matches Python.
  hypercore: "hyperliq:mainnet",
  "hypercore-testnet": "hyperliq:testnet"
};
function deriveCaip2(slug, family, chainIdHex) {
  if (CAIP2_BY_SLUG[slug]) return CAIP2_BY_SLUG[slug];
  if (family === ChainFamily.EVM && chainIdHex) {
    return `eip155:${BigInt(chainIdHex).toString(10)}`;
  }
  return null;
}
var NON_EVM_OVERRIDES = {
  "bitcoin-testnet": { family: ChainFamily.UTXO, decimals: 8, chainIdHex: null, name: "Bitcoin Testnet", nativeToken: "BTC", mainnetSlug: "bitcoin" },
  "solana-devnet": { family: ChainFamily.SOLANA, decimals: 9, chainIdHex: null, name: "Solana Devnet", nativeToken: "SOL", mainnetSlug: "solana" },
  "solana-testnet": { family: ChainFamily.SOLANA, decimals: 9, chainIdHex: null, name: "Solana Testnet", nativeToken: "SOL", mainnetSlug: "solana" },
  "hypercore-testnet": { family: ChainFamily.HYPERCORE, decimals: 8, chainIdHex: null, name: "HyperCore Testnet", nativeToken: "HYPE", mainnetSlug: "hypercore" },
  bitcoin: { family: ChainFamily.UTXO, decimals: 8, chainIdHex: null, name: "Bitcoin", nativeToken: "BTC", aliases: ["bitcoin-mainnet", "0x0"] },
  solana: { family: ChainFamily.SOLANA, decimals: 9, chainIdHex: null, name: "Solana", nativeToken: "SOL", aliases: ["solana-mainnet", "0x1399c"] },
  polkadot: { family: ChainFamily.OTHER, decimals: 10, chainIdHex: null, name: "Polkadot", nativeToken: "DOT", aliases: ["polkadot-mainnet", "0x162"] },
  monero: { family: ChainFamily.UTXO, decimals: 12, chainIdHex: null, name: "Monero", nativeToken: "XMR", aliases: ["monero-mainnet"] },
  zcash: { family: ChainFamily.UTXO, decimals: 8, chainIdHex: null, name: "Zcash", nativeToken: "ZEC", aliases: ["zcash-mainnet"] },
  hypercore: { family: ChainFamily.HYPERCORE, decimals: 8, chainIdHex: null, name: "HyperCore", nativeToken: "HYPE", aliases: ["hypercore-mainnet"] }
};
function layerFor(slug) {
  const folded = slug.toLowerCase();
  const listedIn = (listed) => listed.some((member) => member.toLowerCase() === folded);
  if (listedIn(LAYER1_CHAINS)) return "l1";
  if (listedIn(LAYER2_CHAINS)) return "l2";
  if (listedIn(SIDECHAINS)) return "sidechain";
  return "unknown";
}
function seedFromCanonical(target) {
  for (const [hexId, rawSlug] of Object.entries(CHAIN_MAPPING)) {
    const slug = rawSlug.toLowerCase();
    const config = CHAIN_CONFIG[hexId];
    const token = config?.token ?? "ETH";
    const override = NON_EVM_OVERRIDES[slug];
    const family = override?.family ?? ChainFamily.EVM;
    const decimals = override?.decimals ?? 18;
    const isTestnet = TESTNET_CHAINS.includes(rawSlug);
    let mainnetSlug = null;
    if (isTestnet) {
      const m = TESTNET_TO_MAINNET[rawSlug];
      if (m) mainnetSlug = m.toLowerCase();
    }
    const reorgDepth = REORG_DEPTHS_BY_SLUG[slug] ?? 12;
    const chainIdHex = hexId.toLowerCase();
    const metadata = { layer: layerFor(slug) };
    if (config?.gasUnit) metadata.gasUnit = config.gasUnit;
    if (slug === "etc") {
      metadata.fork_of = "eth";
      metadata.note = "ETC shares pre-fork tx hashes with ETH. Audit sec. 2 calls out this collision risk.";
    }
    try {
      target.register(
        {
          slug,
          chainIdHex,
          family,
          name: config?.name ?? rawSlug,
          nativeToken: token,
          decimals,
          isTestnet,
          mainnetSlug,
          reorgDepth,
          explorerUrl: explorerFor(slug, rawSlug),
          blockTimeSec: BLOCK_TIME_BY_SLUG[slug] ?? (mainnetSlug ? BLOCK_TIME_BY_SLUG[mainnetSlug] ?? null : null),
          caip2: deriveCaip2(slug, family, chainIdHex),
          brandColor: brandColorFor(slug, mainnetSlug),
          logoUrl: LOGO_URL_BY_SLUG[slug] ?? (mainnetSlug ? LOGO_URL_BY_SLUG[mainnetSlug] ?? null : null),
          metadata,
          aliases: foldAliases(override?.aliases, CHAIN_ALIASES[slug])
        },
        { replace: true }
      );
    } catch {
      continue;
    }
  }
  for (const [slug, ovr] of Object.entries(NON_EVM_OVERRIDES)) {
    if (target.get(slug)) continue;
    try {
      target.register(
        {
          slug,
          chainIdHex: ovr.chainIdHex,
          family: ovr.family,
          name: ovr.name,
          nativeToken: ovr.nativeToken,
          decimals: ovr.decimals,
          isTestnet: !!ovr.mainnetSlug,
          mainnetSlug: ovr.mainnetSlug ?? null,
          reorgDepth: REORG_DEPTHS_BY_SLUG[slug] ?? REORG_DEPTHS_BY_SLUG[ovr.mainnetSlug ?? ""] ?? 12,
          explorerUrl: explorerFor(slug, slug),
          blockTimeSec: BLOCK_TIME_BY_SLUG[slug] ?? null,
          caip2: deriveCaip2(slug, ovr.family, ovr.chainIdHex),
          brandColor: brandColorFor(slug, null),
          logoUrl: LOGO_URL_BY_SLUG[slug] ?? null,
          metadata: {},
          aliases: foldAliases(ovr.aliases, CHAIN_ALIASES[slug])
        },
        { replace: true }
      );
    } catch {
      continue;
    }
  }
}
globalSingleton("chains/registry", () => {
  const seeded = new ChainRegistry();
  seedFromCanonical(seeded);
  return seeded;
});
var BASE58 = "123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz";
var BECH32 = "qpzry9x8gf2tvdw0s3jn54khce6mua7l";
var GENERATORS = [996825010, 642813549, 513874426, 1027748829, 705979059];
var BITCOIN_ADDRESS_NETWORKS = {
  bitcoin: { hrp: "bc", versions: [0, 5] },
  "bitcoin-testnet": { hrp: "tb", versions: [111, 196] },
  "bitcoin-regtest": { hrp: "bcrt", versions: [111, 196] }
};
function decodeBase58(address) {
  if (!address || address.length > 90) return null;
  let value = 0n;
  for (const char of address) {
    const digit = BASE58.indexOf(char);
    if (digit < 0) return null;
    value = value * 58n + BigInt(digit);
  }
  const bytes = [];
  while (value) {
    bytes.push(Number(value & 255n));
    value >>= 8n;
  }
  for (const char of address) {
    if (char !== "1") break;
    bytes.push(0);
  }
  return Uint8Array.from(bytes.reverse());
}
var ZCASH_ADDRESS_NETWORKS = {
  zcash: { transparent: [[28, 184], [28, 189]], sapling: "zs", unified: "u" }
};
var MONERO_ADDRESS_NETWORKS = {
  monero: { 95: ["4", "8"], 106: ["4"] }
};
var BECH32_CONST = 1;
var BECH32M_CONST = 734539939;
var SAPLING_PAYLOAD_BYTES = 43;
function decodeBech32(address, maxLength = 90) {
  if (maxLength !== null && address.length > maxLength) return null;
  const lower = address.toLowerCase();
  if (address !== lower && address !== address.toUpperCase()) return null;
  const separator = lower.lastIndexOf("1");
  if (separator < 1) return null;
  const hrp = lower.slice(0, separator);
  const data = [...lower.slice(separator + 1)].map((c) => BECH32.indexOf(c));
  if (data.length < 7 || data.some((v) => v < 0)) return null;
  const expanded = [...hrp].map((c) => c.charCodeAt(0) >> 5).concat([0], [...hrp].map((c) => c.charCodeAt(0) & 31), data);
  let checksum = 1;
  for (const value of expanded) {
    const top = checksum >>> 25;
    checksum = (checksum & 33554431) << 5 ^ value;
    GENERATORS.forEach((generator, bit) => {
      if (top >>> bit & 1) checksum ^= generator;
    });
  }
  if (checksum !== BECH32_CONST && checksum !== BECH32M_CONST) return null;
  return { hrp, data: data.slice(0, -6), constant: checksum };
}
function convertedLength(data) {
  let bits = 0, accumulator = 0, length = 0;
  for (const value of data) {
    accumulator = (accumulator << 5 | value) & 4095;
    bits += 5;
    if (bits >= 8) {
      bits -= 8;
      length++;
    }
  }
  return bits < 5 && (accumulator << 8 - bits & 255) === 0 ? length : null;
}
function base58checkPayload(address) {
  const decoded = decodeBase58(address);
  if (!decoded || decoded.length < 5) return null;
  const checksum = sha256(sha256(decoded.slice(0, -4)));
  return decoded.slice(-4).every((value, index) => value === checksum[index]) ? decoded.slice(0, -4) : null;
}
function validBitcoinAddress(address, network) {
  if (address.length < 14 || address.length > 90) return false;
  const formats = Object.values(BITCOIN_ADDRESS_NETWORKS);
  if (!formats.length) return false;
  if (/^(bc1|tb1|bcrt1)/i.test(address)) {
    const decoded = decodeBech32(address);
    if (!decoded || !formats.some((format) => format.hrp === decoded.hrp) || !decoded.data.length) return false;
    const version = decoded.data[0];
    if (version > 16 || decoded.constant !== (version === 0 ? BECH32_CONST : BECH32M_CONST)) return false;
    const length = convertedLength(decoded.data.slice(1));
    return length !== null && length >= 2 && length <= 40 && (version !== 0 || length === 20 || length === 32);
  }
  const payload = base58checkPayload(address);
  return payload !== null && payload.length === 21 && formats.some((format) => format.versions.includes(payload[0]));
}
function validZcashAddress(address, network = "zcash") {
  const format = ZCASH_ADDRESS_NETWORKS[network];
  if (!format || !address) return false;
  if (address.startsWith("t")) {
    const payload = base58checkPayload(address);
    return payload !== null && payload.length === 22 && format.transparent.some(([a, b]) => payload[0] === a && payload[1] === b);
  }
  const unified = address.toLowerCase().startsWith(`${format.unified}1`);
  const decoded = decodeBech32(address, unified ? null : 90);
  if (!decoded) return false;
  if (decoded.hrp === format.sapling && decoded.constant === BECH32_CONST) {
    return convertedLength(decoded.data) === SAPLING_PAYLOAD_BYTES;
  }
  return decoded.hrp === format.unified && decoded.constant === BECH32M_CONST;
}
function validMoneroAddress(address, network = "monero") {
  const prefixes = MONERO_ADDRESS_NETWORKS[network]?.[address.length];
  return Boolean(prefixes?.some((prefix) => address.startsWith(prefix))) && [...address].every((char) => BASE58.includes(char));
}

// src/chains/address.ts
var RE_EVM = /^0x[0-9a-fA-F]{40}$/;
var RE_BTC_BECH32 = /^(bc1|tb1|bcrt1|zs1)[0-9ac-hj-np-z]{11,87}$/i;
var RE_SOLANA = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/;
var RE_COSMOS_BECH32 = /^[a-z][a-z0-9]{1,10}1[0-9ac-hj-np-z]{38,58}$/;
function addressFamily(address, chainId) {
  if (RE_EVM.test(address)) return ChainFamily.EVM;
  if (validBitcoinAddress(address) || RE_BTC_BECH32.test(address) || validZcashAddress(address) || validMoneroAddress(address)) {
    return ChainFamily.UTXO;
  }
  if (RE_SOLANA.test(address)) return ChainFamily.SOLANA;
  if (RE_COSMOS_BECH32.test(address)) return ChainFamily.COSMOS;
  return null;
}
function normalizeAddress(address, chainId) {
  const addr = address.trim();
  const fam = addressFamily(addr);
  if (hasEvmIdentifiers(fam)) {
    const hex = addr.toLowerCase();
    return hex.startsWith("0x") ? hex : `0x${hex}`;
  }
  if (fam === null && /^0x/i.test(addr)) return addr.toLowerCase();
  if (fam === ChainFamily.UTXO && RE_BTC_BECH32.test(addr) && (addr === addr.toLowerCase() || addr === addr.toUpperCase())) return addr.toLowerCase();
  return addr;
}
function isValidAddress(address, chainId) {
  const addr = address.trim();
  if (!addr) return false;
  const fam = addressFamily(addr);
  switch (fam) {
    case ChainFamily.EVM:
    case ChainFamily.HYPERCORE:
      return RE_EVM.test(addr);
    case ChainFamily.UTXO: {
      return validBitcoinAddress(addr) || validZcashAddress(addr) || validMoneroAddress(addr);
    }
    case ChainFamily.SOLANA:
      return RE_SOLANA.test(addr) && decodeBase58(addr)?.length === 32;
    case ChainFamily.COSMOS:
      return RE_COSMOS_BECH32.test(addr);
    default:
      return RE_EVM.test(addr) || validBitcoinAddress(addr) || validZcashAddress(addr) || validMoneroAddress(addr) || RE_SOLANA.test(addr) && decodeBase58(addr)?.length === 32 || RE_COSMOS_BECH32.test(addr);
  }
}

// src/grouping-rules/specDraft.ts
function locationTable(catalog) {
  return Object.fromEntries((catalog?.valueLocations ?? []).map((l) => [l.name, l.kind]));
}
var DEFAULT_LOCATION = "path";
var PART_MODIFIERS = ["byte_offset", "byte_length", "transform"];
function partKeys(locations) {
  return /* @__PURE__ */ new Set([...Object.keys(locations), ...PART_MODIFIERS]);
}
var SELECTOR_KEYS = /* @__PURE__ */ new Set([
  "role",
  "event",
  "topic0",
  "abci_event",
  "include_call",
  "exclude_log_addresses",
  "value"
]);
var SPEC_KEYS = /* @__PURE__ */ new Set([
  "type",
  "linkage_model",
  "id_label",
  "saga_kind",
  "truth_location",
  "complete_roles",
  "description",
  "selectors",
  "flow",
  "open_ended",
  "max_members"
]);
var FLOW_KEYS = /* @__PURE__ */ new Set([
  "addresses",
  "match",
  "counterparties",
  "direction",
  "group_by",
  "assets",
  "include_calls"
]);
function unmodelled(raw, known) {
  const out = {};
  for (const [key, value] of Object.entries(raw)) {
    if (!known.has(key)) out[key] = value;
  }
  return out;
}
var DEFAULT_FLOW_ID_LABEL = "address_set";
function emptyPart(catalog) {
  return {
    location: DEFAULT_LOCATION,
    operand: "",
    byteOffset: "",
    byteLength: "",
    transform: catalog?.defaultTransform ?? "lower"
  };
}
function emptySelector(catalog, role = "") {
  return {
    role,
    event: "",
    topic0: "",
    abciEvent: "",
    includeCall: false,
    excludeLogAddresses: "",
    value: [emptyPart(catalog)]
  };
}
function emptyFlow(catalog) {
  return {
    addresses: "",
    match: catalog?.flow?.matches[0]?.value ?? "between",
    counterparties: "",
    direction: catalog?.flow?.directions[0]?.value ?? "any",
    groupBy: catalog?.flow?.groupBys[0]?.value ?? "rule",
    assets: "",
    includeCalls: true
  };
}
function emptyDraft(catalog) {
  const [source, destination] = catalog?.roles.canonical ?? ["source", "destination"];
  return {
    matchBy: "id",
    flow: emptyFlow(catalog),
    openEnded: false,
    maxMembers: "",
    type: catalog?.relationshipTypes[0]?.value ?? "cross_chain_message",
    linkageModel: catalog?.linkageModels[0]?.value ?? "deterministic",
    idLabel: "",
    sagaKind: "",
    truthLocation: catalog?.truthLocations[0]?.value ?? "decoded_args",
    completeRoles: (catalog?.roles.defaultCompleteRoles ?? ["source", "destination"]).join(", "),
    description: "",
    selectors: [
      emptySelector(catalog, source ?? "source"),
      emptySelector(catalog, destination ?? "destination")
    ],
    locations: locationTable(catalog)
  };
}
function splitList(text) {
  return text.split(",").map((part) => part.trim()).filter(Boolean);
}
function splitAddresses(text) {
  return text.split(/[\s,;]+/).map((part) => part.trim()).filter(Boolean);
}
function withMatchBy(draft, matchBy, catalog) {
  if (draft.matchBy === matchBy) return draft;
  const flowType = catalog?.flow?.relationshipType ?? "address_flow";
  const idType = catalog?.relationshipTypes[0]?.value ?? "cross_chain_message";
  return {
    ...draft,
    matchBy,
    type: matchBy === "addresses" ? flowType : draft.type === flowType ? idType : draft.type,
    idLabel: matchBy === "addresses" ? draft.idLabel || DEFAULT_FLOW_ID_LABEL : draft.idLabel === DEFAULT_FLOW_ID_LABEL ? "" : draft.idLabel
  };
}
function locationsIn(part, locations) {
  return Object.entries(locations).filter(([name, kind]) => {
    const value = part[name];
    if (kind === "flag") return value === true;
    if (kind === "integer") return value !== void 0 && value !== null;
    return Boolean(value);
  }).map(([name]) => name);
}
function locationOf(part, locations) {
  return locationsIn(part, locations)[0] ?? DEFAULT_LOCATION;
}
function operandOf(part, location, locations) {
  if (locations[location] === "flag") return "";
  const raw = part[location];
  return raw === void 0 || raw === null ? "" : String(raw);
}
function specToDraft(spec2, catalog) {
  const base = emptyDraft(catalog);
  const { locations } = base;
  const modelledPartKeys = partKeys(locations);
  const selectors = Array.isArray(spec2.selectors) ? spec2.selectors : [];
  const storedFlow = spec2.flow && typeof spec2.flow === "object" ? asRecord(spec2.flow) : null;
  return {
    matchBy: storedFlow ? "addresses" : "id",
    flow: storedFlow ? {
      addresses: asStringList(storedFlow.addresses).join("\n"),
      match: asStringOr(storedFlow.match, base.flow.match),
      counterparties: asStringList(storedFlow.counterparties).join("\n"),
      direction: asStringOr(storedFlow.direction, base.flow.direction),
      groupBy: asStringOr(storedFlow.group_by, base.flow.groupBy),
      assets: asStringList(storedFlow.assets).join(", "),
      includeCalls: storedFlow.include_calls !== false,
      passthrough: unmodelled(storedFlow, FLOW_KEYS)
    } : base.flow,
    openEnded: spec2.open_ended === true,
    maxMembers: typeof spec2.max_members === "number" ? String(spec2.max_members) : "",
    type: String(spec2.type ?? base.type),
    linkageModel: String(spec2.linkage_model ?? base.linkageModel),
    idLabel: String(spec2.id_label ?? ""),
    sagaKind: String(spec2.saga_kind ?? ""),
    truthLocation: String(spec2.truth_location ?? base.truthLocation),
    completeRoles: (Array.isArray(spec2.complete_roles) ? spec2.complete_roles : []).join(", "),
    description: String(spec2.description ?? ""),
    locations,
    passthrough: unmodelled(spec2, SPEC_KEYS),
    selectors: selectors.length ? selectors.map((entry) => {
      const raw = asRecord(entry);
      const parts = Array.isArray(raw.value) ? raw.value : [];
      return {
        role: asStringOr(raw.role),
        event: asStringOr(raw.event),
        topic0: asStringOr(raw.topic0),
        abciEvent: asStringOr(raw.abci_event),
        includeCall: raw.include_call === true,
        excludeLogAddresses: asStringList(raw.exclude_log_addresses).join(", "),
        passthrough: unmodelled(raw, SELECTOR_KEYS),
        value: parts.length ? parts.map((entryPart) => {
          const p = asRecord(entryPart);
          const present = locationsIn(p, locations);
          const location = present[0] ?? DEFAULT_LOCATION;
          return {
            location,
            droppedLocations: present.slice(1),
            operand: operandOf(p, location, locations),
            byteOffset: p.byte_offset ? String(p.byte_offset) : "",
            byteLength: p.byte_length === void 0 || p.byte_length === null ? "" : String(p.byte_length),
            transform: asStringOr(p.transform, catalog?.defaultTransform ?? "lower"),
            passthrough: unmodelled(p, modelledPartKeys)
          };
        }) : [emptyPart(catalog)]
      };
    }) : base.selectors
  };
}
function partToWire(part, locations) {
  const out = {
    ...part.passthrough ?? {},
    transform: part.transform
  };
  const kind = locations[part.location] ?? "text";
  if (kind === "flag") {
    out[part.location] = true;
  } else if (kind === "integer") {
    out[part.location] = Number(part.operand);
  } else {
    out[part.location] = part.operand.trim();
  }
  if (part.location === "data_bytes_word") {
    if (part.byteOffset.trim()) out.byte_offset = Number(part.byteOffset);
    if (part.byteLength.trim()) out.byte_length = Number(part.byteLength);
  }
  return out;
}
function flowToWire(flow, touching) {
  const out = {
    ...flow.passthrough ?? {},
    addresses: splitAddresses(flow.addresses),
    match: flow.match,
    group_by: flow.groupBy,
    include_calls: flow.includeCalls
  };
  delete out.counterparties;
  delete out.direction;
  if (touching) {
    out.direction = flow.direction;
    const counterparties = splitAddresses(flow.counterparties);
    if (counterparties.length) out.counterparties = counterparties;
  }
  const assets = splitList(flow.assets);
  if (assets.length) out.assets = assets;
  else delete out.assets;
  return out;
}
function draftToSpec(draft) {
  const maxMembers = Number(draft.maxMembers.trim());
  const withLimits = (spec3) => {
    delete spec3.max_members;
    if (draft.maxMembers.trim() && Number.isInteger(maxMembers) && maxMembers > 0) {
      spec3.max_members = maxMembers;
    }
    if (draft.description.trim()) spec3.description = draft.description.trim();
    return spec3;
  };
  if (draft.matchBy === "addresses") {
    return withLimits({
      ...draft.passthrough ?? {},
      type: draft.type,
      linkage_model: draft.linkageModel,
      id_label: draft.idLabel.trim() || DEFAULT_FLOW_ID_LABEL,
      saga_kind: draft.sagaKind.trim(),
      flow: flowToWire(draft.flow, draft.flow.match === "touching"),
      open_ended: true
    });
  }
  const spec2 = {
    ...draft.passthrough ?? {},
    type: draft.type,
    linkage_model: draft.linkageModel,
    id_label: draft.idLabel.trim(),
    saga_kind: draft.sagaKind.trim(),
    truth_location: draft.truthLocation,
    selectors: draft.selectors.map((selector) => {
      const out = {
        ...selector.passthrough ?? {},
        role: selector.role.trim(),
        include_call: selector.includeCall,
        value: selector.value.map((part) => partToWire(part, draft.locations))
      };
      if (selector.event.trim()) out.event = selector.event.trim();
      if (selector.topic0.trim()) out.topic0 = selector.topic0.trim().toLowerCase();
      if (selector.abciEvent.trim()) out.abci_event = selector.abciEvent.trim();
      const excluded = splitList(selector.excludeLogAddresses).map((a) => a.toLowerCase());
      if (excluded.length) out.exclude_log_addresses = excluded;
      return out;
    })
  };
  const roles = splitList(draft.completeRoles);
  if (roles.length && !draft.openEnded) spec2.complete_roles = roles;
  if (draft.openEnded) spec2.open_ended = true;
  else delete spec2.open_ended;
  return withLimits(spec2);
}
function firstInvalidAddress(entries) {
  return entries.find((entry) => !isValidAddress(entry));
}
function validateFlow(draft, errors) {
  const flow = draft.flow;
  const touching = flow.match === "touching";
  const addresses = [...new Set(splitAddresses(flow.addresses).map((a) => normalizeAddress(a)))];
  const badAddress = firstInvalidAddress(splitAddresses(flow.addresses));
  if (badAddress) {
    errors.addresses = `\u201C${badAddress}\u201D is not an address.`;
  } else if (addresses.length === 0) {
    errors.addresses = "List the addresses this rule is about.";
  } else if (!touching && addresses.length < 2) {
    errors.addresses = "\u201CBetween\u201D needs two or more addresses.";
  }
  const badCounterparty = firstInvalidAddress(splitAddresses(flow.counterparties));
  if (touching && badCounterparty) {
    errors.counterparties = `\u201C${badCounterparty}\u201D is not an address.`;
  }
  const badAsset = splitList(flow.assets).find(
    (asset) => asset.toLowerCase() !== "native" && !isValidAddress(asset)
  );
  if (badAsset) {
    errors.assets = `\u201C${badAsset}\u201D is not a token contract (or \u201Cnative\u201D).`;
  }
  if (!touching && flow.groupBy === "counterparty") {
    errors.groupBy = "One group per counterparty needs \u201CTouching\u201D \u2014 between listed addresses there is no outside address.";
  }
}
function validateDraft(draft) {
  const errors = {};
  if (draft.maxMembers.trim()) {
    const n = Number(draft.maxMembers.trim());
    if (!Number.isInteger(n) || n < 1 || n > 5e3) {
      errors.maxMembers = "A whole number from 1 to 5000.";
    }
  }
  if (draft.matchBy === "addresses") {
    if (!draft.sagaKind.trim()) {
      errors.sagaKind = "The namespace this rule competes for. One rule applies per kind.";
    }
    validateFlow(draft, errors);
    return errors;
  }
  if (!draft.idLabel.trim()) {
    errors.idLabel = "Name the id the legs share \u2014 it is what the group is keyed on.";
  }
  if (!draft.sagaKind.trim()) {
    errors.sagaKind = "The namespace this rule competes for. One rule applies per kind.";
  }
  const declared = splitList(draft.completeRoles).map(canonRole).filter(Boolean);
  if (declared.length && !draft.openEnded) {
    const produced = new Set(
      draft.selectors.map((selector) => canonRole(selector.role)).filter(Boolean)
    );
    const unreachable = [...new Set(declared)].filter((role) => !produced.has(role));
    if (unreachable.length) {
      errors.completeRoles = `No selector produces ${unreachable.join(", ")}. The group would wait for a leg this rule cannot emit, then time out for good.`;
    }
  }
  const selectorErrors = {};
  draft.selectors.forEach((selector, index) => {
    if (!selector.role.trim()) {
      selectorErrors[index] = "Every selector names the leg it identifies.";
      return;
    }
    if (selector.value.length === 0) {
      selectorErrors[index] = "A selector with no value part resolves nothing.";
      return;
    }
    const incomplete = selector.value.some(
      (part) => draft.locations[part.location] !== "flag" && !part.operand.trim()
    );
    if (incomplete) {
      selectorErrors[index] = "Every value part needs something to read.";
      return;
    }
    const badNumber = selector.value.some(
      (part) => draft.locations[part.location] === "integer" && !Number.isInteger(Number(part.operand))
    );
    if (badNumber) selectorErrors[index] = "A word index is a whole number.";
  });
  if (Object.keys(selectorErrors).length) errors.selectors = selectorErrors;
  return errors;
}
function draftIsValid(draft) {
  return Object.keys(validateDraft(draft)).length === 0;
}

export { DEFAULT_FLOW_ID_LABEL, createApiGroupingRulesSource, deletionImpact, draftIsValid, draftToSpec, emptyDraft, emptyFlow, emptyPart, emptySelector, flowSummary, groupBySagaKind, isValid, locationOf, locationTable, neverMatchedNote, parseSpec, requiredSpecFields, scopeOptions, specToDraft, splitAddresses, standingOf, toCreateInput, toUpdateInput, validate, validateDraft, withMatchBy };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map